import { gql, DocumentNode, TypedDocumentNode } from '@apollo/client';
import { Company } from 'services/GraphQl';
import { HrisConnection } from 'services/Interfaces';
import { BulkCaseCreateInput } from 'services/GraphQl';
import { WHEN_BENEFITS_QUERY } from './queries/whenBenefits';
import { GET_USER, GET_MARKETPLACE_SERVICES } from './queries/user';

interface _Queries {
  // lookupEmployee: DocumentNode
  SEARCH_PLANS: DocumentNode;
  INDIVIDUAL_RATE: DocumentNode;
  GET_GEOGRAPHY: DocumentNode;
  listUsers: DocumentNode;
  getRates: DocumentNode;
  getUser: DocumentNode;
  getMarketplaceServices: DocumentNode;
  subsidyCheck: TypedDocumentNode<
    { subsidyCheck: { federalSubsidyAmount: number } },
    { uid: string }
  >;
  generateEnrollmentUrl: DocumentNode;
  aiRecommendation: DocumentNode;
  reScorePlans: DocumentNode;
  adminGetCase: TypedDocumentNode<
    { adminGetCase: { caseId: string; expiration: string; companyId: string } },
    { caseId: string; companyId: string; reason: string }
  >;
  adminGetUser: TypedDocumentNode<
    { adminGetUser: { userId: string; expiration: string; companyId: string } },
    { userId: string; reason: string }
  >;
  getLinkToken: TypedDocumentNode<
    { hris: { merge: { getLinkToken: { token: string } } } },
    {
      companyId: string;
      companyName: string;
      emailAddress: string;
      integrationMethodId: string;
    }
  >;
  hrisConnectionDetails: TypedDocumentNode<
    { hrisConnectionDetails: HrisConnection },
    { companyId?: string; adminSearch?: boolean }
  >;
  confirmExitCodeIdentity: TypedDocumentNode<
    { confirmExitCodeIdentity: boolean },
    {
      companyId: string;
      exitCode?: string;
      lastName: string;
      birthDate: string;
      zipCode?: string;
      workEmail?: string;
      terminationDate?: string;
    }
  >;
  getSearchToken: TypedDocumentNode<
    { getSearchToken: string },
    { companyId?: string; adminSearch?: boolean }
  >;
  // getApplicationSearchToken: TypedDocumentNode<{ getApplicationSearchToken: string }>
  fetchDecryptedSSN: TypedDocumentNode<
    { fetchDecryptedSSN: string },
    {
      applicationUserId: string;
      applicationId: string;
      householdMemberId: string;
    }
  >;
  getApplicationNotes: TypedDocumentNode<
    {
      getApplicationNotes: {
        id: string;
        userName: string;
        date: string;
        note: string;
      }[];
    },
    { applicationUserId: string; applicationId: string }
  >;
  estimateImpact: TypedDocumentNode<
    {
      hris: {
        csvUpload: {
          estimateImpact: {
            new: number;
            missing: number;
            total: number;
            existing: number;
          };
        };
      };
    },
    { companyId: string; ids: Array<string> }
  >;
  exitCodeInfo: TypedDocumentNode<
    {
      exitCodeInfo: {
        firstName: string;
        documents: number;
        whenBenefit: number;
        servicesEnabled: string[];
      };
    },
    { companyId: string; exitCode: string }
  >;
  caseEstimateImpact: TypedDocumentNode<
    {
      caseEstimateImpact: {
        employeesToBeCreated: number;
        employeesExists: number;
        casesToBeCreated: number;
        caseExistsNoUpdates: number;
        casesToBeUpdated: number;
      };
    },
    { csvData: BulkCaseCreateInput[] }
  >;
  getUserBankInfo: TypedDocumentNode<
    {
      getUserBankInfo: {
        accountNumber: string;
        routingNumber: string;
        nameOnAccount: string;
      };
    },
    { userId: string }
  >;
  company: TypedDocumentNode<{ company: Company }, { companyId: string }>;
  whenBenefits: TypedDocumentNode<
    {
      whenBenefits: {
        caseId: string;
        companyId: string;
        benefit: {
          status: string;
          hasBenefit: boolean;
          benefitTier: string;
          amount: number;
          releasedDate: string;
          detail: {
            total: number;
            amountSpent: number;
            amountPending: number;
            amountRemaining: number;
            amountForfeited: number;
          };
          distributionType: string;
          unusedFunds: number;
          monthsToAllocate: number;
          months: {
            month: string;
            amount: number;
            deadline: string;
            requested: number;
            reimbursedAmount: number;
            expired: boolean;
            returned: boolean;
          }[];
          perMonthDeadline: {
            dayOfMonth: number;
            monthsAfter: number;
          };
          reimbursementRequests: {
            reimbursementRequestId: string;
            createdAt: string;
            updatedAt: string;
            userId: string;
            amount: number;
            status: string;
            approvedAmount: number;
            coverageMonths: string[];
            files: {
              name: string;
              type: string;
              path: string;
            }[];
          }[];
          monthlyBenefitInitialGracePeriod: {
            dayOfMonth: number;
            monthsAfter: number;
          };
        };
      }[];
    },
    { uid: string }
  >;
  getMixpanelData: TypedDocumentNode<
    {
      getMixpanelData: {
        data: {
          events: {
            event: string;
            values: {
              date: string;
              value: number;
            }[];
          }[];
        };
      };
    },
    { events: string; fromDate: string; toDate: string }
  >;
  getAllTimeReport: TypedDocumentNode<
    {
      getAllTimeReport: {
        totalCases: number;
        wbTotal: number;
        wbRemaining: number;
        wbSpent: number;
        wbForfeited: number;
        activeCases: number;
        openCases: number;
        closedCases: number;
        closedInactiveCases: number;
        closedResolvedCases: number;
        cancelledResolvedCases: number;
        activatedCases: number;
        healthDecisionACA: number;
        healthDecisionCobra: number;
        healthDecisionUnknown: number;
        healthDecisionOther: number;
      };
    },
    {}
  >;
  getCaseStateByCreatedAt: TypedDocumentNode<
    {
      getCaseStateByCreatedAt: {
        data: {
          createdAt: string;
          totalCases: number;
          totalCompanies: number;
          companyIds: string[];
          wbTotal: number;
          wbRemaining: number;
          wbSpent: number;
          wbForfeited: number;
          activeCases: number;
          openCases: number;
          casesSharedOnTime: number;
          klaviyoCasesShared: number;
          zendeskCasesShared: number;
          closedCases: number;
          closedInactiveCases: number;
          closedResolvedCases: number;
          cancelledResolvedCases: number;
          activatedCases: number;
          healthDecisionACA: number;
          healthDecisionCobra: number;
          healthDecisionUnknown: number;
          healthDecisionOther: number;
        }[];
        lastUpdated: string;
      };
    },
    { fromDate: string; toDate: string }
  >;
  getCobraAdminDetails: TypedDocumentNode<
    {
      getCobraAdminDetails: {
        name: string;
        phone: string | null;
        website: string | null;
      };
    },
    { cobraAdminName: string }
  >;
  getCompanySettings: DocumentNode;
}

function getQueries(): _Queries {
  let Queries: any = {
    company: gql`
      query Company($companyId: String!) {
        company(companyId: $companyId) {
          code
          address {
            street1
            street2
            city
            state
            zip
          }
          emailDomain
          servicesEnabled
          hrisSetup
          landingPages
          logo
          name
          servicesEnabled
          terminationDate
          zendeskOrganizationId
          createdOn
        }
      }
    `,
    whenBenefits: WHEN_BENEFITS_QUERY,
    case: gql`
      query Case($companyId: String!, $caseId: String!) {
        case(companyId: $companyId, caseId: $caseId) {
          id
          caseId
          name
          caseType
          companyId
          status
          createdAt
          createdBy
          updatedAt
          updatedBy
          closeDate
          secure
          shareDate
          sharedOn
          scheduled
          shared
          sharedStatus
          klaviyoCaseShared
          klaviyo {
            caseShared {
              date
            }
          }
          zendeskTicket
          zendesk {
            ticketId
            date
          }
          tasks {
            open
            closed
            total
          }
          healthInsuranceDecision
          cobraElectionReason
          exitMeetingDate
          released
          ref
          benefitTier
          lastDay
          terminationDate
          reason
          terminationType
          dataVersion
          syncUser {
            id
            date
          }
          contact {
            id
            firstName
            lastName
            email
            phone
            dateOfBirth
            address {
              street1
              street2
              city
              state
              zip
            }
            salary
            gender
            status
            ethnicity
            maritalStatus
            flsaStatus
            payFrequency
            payRate
            payCurrency
            title
            hireDate
            terminationDate
          }
          cobranding {
            logo
            name
            companyCode
          }
          childCompany {
            id
            name
            logoUrl
            contact {
              name
              email
              phone
            }
          }
          event {
            type
            date
            termination {
              type
              reason
            }
            cobra {
              qualifyingEvent
              estimate
              currentContribution
            }
          }
          household {
            firstName
            lastName
            dateOfBirth
            relationship
          }
          benefits {
            type
            name
            carrier
            monthlyEmployeeContribution
            monthlyEmployerContribution
            benefitEndDate
            dependents {
              firstName
              lastName
              dateOfBirth
              relationship
            }
          }
          services {
            type
            whenBenefitAmount
          }
          whenBenefit {
            status
            hasBenefit
            benefitTier
            amount
            releasedDate
            detail {
              total
              amountSpent
              amountPending
              amountRemaining
              amountForfeited
            }
            distributionType
            unusedFunds
            monthsToAllocate
          }
          healthInsuranceDecisionDetail
          assignee
          syncStatus
          syncedAt
          servicesEnabled
        }
      }
    `,
    cases: gql`
      query Cases($limit: Int, $offset: Int, $filters: CasesFiltersInput) {
        cases(limit: $limit, offset: $offset, filters: $filters) {
          id
          caseId
          name
          caseType
          companyId
          status
          createdAt
          createdBy
          updatedAt
          updatedBy
          closeDate
          secure
          shareDate
          sharedOn
          scheduled
          shared
          sharedStatus
          klaviyoCaseShared
          klaviyo {
            caseShared {
              date
            }
          }
          zendeskTicket
          zendesk {
            ticketId
            date
          }
          tasks {
            open
            closed
            total
          }
          healthInsuranceDecision
          cobraElectionReason
          exitMeetingDate
          released
          ref
          benefitTier
          lastDay
          terminationDate
          reason
          terminationType
          dataVersion
          syncUser {
            id
            date
          }
          contact {
            id
            firstName
            lastName
            email
            phone
            dateOfBirth
            address {
              street1
              street2
              city
              state
              zip
            }
            salary
            gender
            status
            ethnicity
            maritalStatus
            flsaStatus
            payFrequency
            payRate
            payCurrency
            title
            hireDate
            terminationDate
          }
          cobranding {
            logo
            name
            companyCode
          }
          childCompany {
            id
            name
            logoUrl
            contact {
              name
              email
              phone
            }
          }
          event {
            type
            date
            termination {
              type
              reason
            }
            cobra {
              qualifyingEvent
              estimate
              currentContribution
            }
          }
          household {
            firstName
            lastName
            dateOfBirth
            relationship
          }
          benefits {
            type
            name
            carrier
            monthlyEmployeeContribution
            monthlyEmployerContribution
            benefitEndDate
            dependents {
              firstName
              lastName
              dateOfBirth
              relationship
            }
          }
          services {
            type
            whenBenefitAmount
          }
          whenBenefit {
            status
            hasBenefit
            benefitTier
            amount
            releasedDate
            detail {
              total
              amountSpent
              amountPending
              amountRemaining
              amountForfeited
            }
            distributionType
            unusedFunds
            monthsToAllocate
          }
          healthInsuranceDecisionDetail
          assignee
          syncStatus
          syncedAt
        }
      }
    `,
    getUserBankInfo: gql`
      query getUserBankInfo($userId: String!) {
        getUserBankInfo(userId: $userId) {
          accountNumber
          routingNumber
          nameOnAccount
        }
      }
    `,
    exitCodeInfo: gql`
      query exitCodeInfo($companyId: String!, $exitCode: String!) {
        exitCodeInfo(companyId: $companyId, exitCode: $exitCode) {
          firstName
          documents
          whenBenefit
          servicesEnabled
        }
      }
    `,
    messages: gql`
      query messages {
        messages {
          id
          text
          user
          timestamp
        }
      }
    `,
    adminGetCase: gql`
      query adminGetCase(
        $caseId: String!
        $companyId: String!
        $reason: HIPAAViewReasons
      ) {
        adminGetCase(caseId: $caseId, companyId: $companyId, reason: $reason) {
          caseId
          companyId
          expiration
        }
      }
    `,
    adminGetUser: gql`
      query adminGetUser($userId: String!, $reason: HIPAAViewReasons) {
        adminGetUser(userId: $userId, reason: $reason) {
          userId
          companyId
          expiration
        }
      }
    `,
    estimateImpact: gql`
      query Hris($companyId: String, $ids: [String]) {
        hris {
          csvUpload {
            estimateImpact(companyId: $companyId, ids: $ids) {
              missing
              new
              total
              existing
            }
          }
        }
      }
    `,
    caseEstimateImpact: gql`
      query caseEstimateImpact($csvData: [BulkCaseCreateInput!]!) {
        caseEstimateImpact(csvData: $csvData) {
          employeesToBeCreated
          employeesExists
          casesToBeCreated
          caseExistsNoUpdates
          casesToBeUpdated
        }
      }
    `,
    getSearchToken: gql`
      query getSearchToken($companyId: String, $adminSearch: Boolean) {
        getSearchToken(companyId: $companyId, adminSearch: $adminSearch)
      }
    `,
    //   getApplicationSearchToken: gql`
    //   query getApplicationSearchToken {
    //     getApplicationSearchToken
    //   }
    // `,
    fetchDecryptedSSN: gql`
      query fetchDecryptedSSN(
        $applicationUserId: String!
        $applicationId: ID!
        $householdMemberId: String!
      ) {
        fetchDecryptedSSN(
          applicationUserId: $applicationUserId
          applicationId: $applicationId
          householdMemberId: $householdMemberId
        )
      }
    `,
    getApplicationNotes: gql`
      query getApplicationNotes(
        $applicationUserId: String!
        $applicationId: ID!
      ) {
        getApplicationNotes(
          applicationUserId: $applicationUserId
          applicationId: $applicationId
        ) {
          id
          userName
          date
          note
        }
      }
    `,
    hrisConnectionDetails: gql`
      query hrisConnectionDetails($connectionId: String, $companyId: String) {
        hrisConnectionDetails(
          connectionId: $connectionId
          companyId: $companyId
        ) {
          name
          logo
          squareLogo
          status
          lastImport
          createdOn
        }
      }
    `,
    confirmExitCodeIdentity: gql`
      query confirmExitCodeIdentity(
        $companyId: String!
        $exitCode: String
        $lastName: String!
        $birthDate: String!
        $zipCode: String
        $workEmail: String
        $terminationDate: String
      ) {
        confirmExitCodeIdentity(
          companyId: $companyId
          exitCode: $exitCode
          lastName: $lastName
          birthDate: $birthDate
          zipCode: $zipCode
          workEmail: $workEmail
          terminationDate: $terminationDate
        )
      }
    `,
    reScorePlans: gql`
      query reScorePlans($effectiveDate: String, $uid: String) {
        reScorePlans(effectiveDate: $effectiveDate, uid: $uid) {
          total
        }
      }
    `,
    aiRecommendation: gql`
      query aiRecommendation($effectiveDate: String, $planId: String) {
        aiRecommendation(effectiveDate: $effectiveDate, planId: $planId) {
          aiScore
          pros
          cons
          headline
          explanation
        }
      }
    `,
    generateEnrollmentUrl: gql`
      query generateEnrollmentUrl(
        $effectiveDate: String
        $planId: String
        $carrierId: String
      ) {
        generateEnrollmentUrl(
          effectiveDate: $effectiveDate
          planId: $planId
          carrierId: $carrierId
        ) {
          redirectUrl
        }
      }
    `,
    getLinkToken: gql`
      query GetLinkToken(
        $companyId: String!
        $companyName: String!
        $emailAddress: String!
        $integrationMethodId: String!
      ) {
        hris {
          merge {
            getLinkToken(
              companyId: $companyId
              companyName: $companyName
              emailAddress: $emailAddress
              integrationMethodId: $integrationMethodId
            ) {
              token
            }
          }
        }
      }
    `,
    getRates: gql`
      query getRates($effectiveDate: String, $uid: String, $source: String) {
        getRates(effectiveDate: $effectiveDate, uid: $uid, source: $source) {
          total
        }
      }
    `,
    subsidyCheck: gql`
      query SubsidyCheck($uid: String!) {
        subsidyCheck(uid: $uid) {
          federalSubsidyAmount
        }
      }
    `,
    //   lookupEmployee: gql`
    //   query lookupEmployee($company: String, $lastName: String, $dateOfBirth: String, $email: String){
    //   lookupEmployee(company: $company, lastName: $lastName, email: $email, dateOfBirth: $dateOfBirth){
    //     firstName
    //     lastName
    //     employeeId
    //     benefit
    //   }
    // }
    // `,
    
    getCompanySettings: gql`
      query getCompanySettings($companyId: String!) {
        company(companyId: $companyId) {
          name
          address {
            street1
            street2
            city
            state
            zip
          }
          headquarterState
          planRenewalMonth
          activationDate
          terminationDate
          customerSuccessManager
          cobraAdministrationProvider
          emailDomain
          logo
          fein
          companyPointOfContact {
            name
            email
            phone
            title
          }
          employeeCount
          externalCompanyIdentifier
          servicesEnabled
          whenBenefitUseOnCobra
          automation {
            employerPlatformLessWorkflowEnable
            numberOfDays
            errorNotificationContactEmail
          }
          timing {
            betweenRequests {
              value
              measure
            }
            firstRequest {
              value
              measure
            }
          }
          monthlyWhenBenefitSettings {
            perMonthDeadline {
              dayOfMonth
              monthsAfter
            }
            monthlyBenefitInitialGracePeriod {
              dayOfMonth
              monthsAfter
            }
            unusedFunds
          }
        }
      }
    `,
    getMixpanelData: gql`
      query getMixpanelData($events: String!, $fromDate: String!, $toDate: String!) {
        getMixpanelData(events: $events, fromDate: $fromDate, toDate: $toDate) {
          data {
            events {
              event
              values {
                date
                value
              }
            }
          }
        }
      }
    `,
    getAllTimeReport: gql`
      query getAllTimeReport {
        getAllTimeReport {
          totalCases
          wbTotal
          wbRemaining
          wbSpent
          wbForfeited
          activeCases
          openCases
          closedCases
          closedInactiveCases
          closedResolvedCases
          cancelledResolvedCases
          activatedCases
          healthDecisionACA
          healthDecisionCobra
          healthDecisionUnknown
          healthDecisionOther
          HID_ACA_ON_EXCHANGE_EXTERNAL
          HID_ACA_ON_EXCHANGE_VELAPOINT
          HID_ACA_ON_EXCHANGE_WHEN
          HID_COBRA
          HID_DECLINE_TO_DISCLOSE
          HID_MEDICAID_EXTERNAL
          HID_MEDICARE_EXTERNAL
          HID_MEDICARE_VELAPOINT
          HID_MEDICARE_WHEN
          HID_NEW_EMPLOYMENT
          HID_OFF_EXCHANGE_EXTERNAL
          HID_OFF_EXCHANGE_VELAPOINT
          HID_OFF_EXCHANGE_WHEN
          HID_OTHER_EXTERNAL_COVERAGE
          HID_PARENTS
          HID_PENDING
          HID_SHORT_TERM_EXTERNAL
          HID_SHORT_TERM_VELAPOINT
          HID_SHORT_TERM_WHEN
          HID_SPOUSE
          HID_UNINSURED
          HID_UNKNOWN_CASE_CLOSED
        }
      }
    `,
    getCaseStateByCreatedAt: gql`
      query getCaseStateByCreatedAt($fromDate: String!, $toDate: String!) {
        getCaseStateByCreatedAt(fromDate: $fromDate, toDate: $toDate) {
          data {
            createdAt
            totalCases
            totalCompanies
            companyIds
            wbTotal
            wbRemaining
            wbSpent
            wbForfeited
            activeCases
            openCases
            casesSharedOnTime
            klaviyoCasesShared
            zendeskCasesShared
            closedCases
            closedInactiveCases
            closedResolvedCases
            cancelledResolvedCases
            activatedCases
            healthDecisionACA
            healthDecisionCobra
            healthDecisionUnknown
            healthDecisionOther
          }
          lastUpdated
        }
      }
    `,
    getCobraAdminDetails: gql`
      query getCobraAdminDetails($cobraAdminName: String!) {
        getCobraAdminDetails(cobraAdminName: $cobraAdminName) {
          name
          phone
          website
        }
      }
    `,
  };

  Queries.listUsers = gql`
    query listUsers($filters: String, $page: Int, $limit: Int) {
      listUsers(filters: $filters, page: $page, limit: $limit) {
        users {
          uid
          email
          admin
        }
      }
    }
  `;

  Queries.getUser = GET_USER;
  Queries.getMarketplaceServices = GET_MARKETPLACE_SERVICES;

  try {
    Queries.GET_GEOGRAPHY = gql`
      query geographyByZip($zipcode: String) {
        geographyByZip(zipcode: $zipcode) {
          zipcode
          name
          fips
          state
        }
      }
    `;
  } catch (err) {
    console.error('GET_GEOGRAPHY error', err);
  }

  try {
    Queries.SEARCH_PLANS = gql`
      query SearchPlansQuery($filters: String, $page: Int) {
        searchPlans(filters: $filters, page: $page) {
          plans {
            premium
            name
            provider
            id
            source
            type
            maxOutOfPocket
            deductible
            metal
            rating
            market
            diseasePrograms
            benefits {
              name
              covered
              inNetwork
              outOfNetwork
            }
            benefitUrl
            formularyUrl
            brochureUrl
            networkUrl
          }
          total
        }
      }
    `;
  } catch (err) {
    console.error('SEARCH_PLANS error', err);
  }
  return Queries;
}

const Queries: _Queries = getQueries();
export { Queries };
